import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import LineActions from "./LineActions";
import { clozeIndexFor } from "./cloze";

// The lyrics are the product, so they get the middle of the screen and
// everything else is wallpaper behind them.
//
// Three views off one timeline, which is the payoff for resolving word timings
// at generation time rather than in the browser:
//
//   read   - sung line, word highlighted as it lands
//   cloze  - one word per line held back, fading in as it's sung
//
// English is a separate toggle rather than a fourth mode, so it can be combined
// with either - reading cloze lines with the translation showing is a different
// exercise from reading them without, and both are worth having.
//
// Lines carry startMs/endMs whether or not ElevenLabs returned word timestamps;
// without them `words` is empty and this degrades to line-level highlighting.

// A clozed word renders like any other - same element, same text, same width -
// and only its colour is held back. It starts fading in the moment the word is
// sung, slowly enough that the fade is itself the window to guess in.
// Told whether it has been sung rather than working it out from the clock, so a
// line that isn't the one playing can still be drawn correctly - every word on
// an earlier line is sung, every word on a later one is not.
function Word({ cloze, highlighted, sung, word }) {
  const classes = [
    "ir-word",
    sung ? "ir-word-sung" : "",
    highlighted ? "ir-word-active" : "",
    cloze ? "ir-word-cloze" : "",
    cloze && sung ? "ir-word-cloze-shown" : "",
  ].filter(Boolean).join(" ");

  return <span className={classes}>{word.text}</span>;
}

Word.propTypes = {
  cloze: PropTypes.bool.isRequired,
  highlighted: PropTypes.bool.isRequired,
  sung: PropTypes.bool.isRequired,
  word: PropTypes.shape({ endMs: PropTypes.number, startMs: PropTypes.number, text: PropTypes.string }).isRequired,
};

// Memoised, and only the line being sung is given the clock - see Lyrics below.
// Without this, every rendered line re-runs its word timings ~16 times a second
// for text nobody is reading.
const Line = React.memo(React.forwardRef(function Line(
  { active, line, mode, onAdd, onPlayFrom, passed, positionMs, showEnglish }, ref
) {
  const clozeIndex = useMemo(() => (mode === "cloze" ? clozeIndexFor(line) : -1), [line, mode]);
  const hasWords = line.words && line.words.length > 0;
  // Word spans are needed wherever there's a blank to hold open, not just on the
  // line being sung - otherwise every upcoming line gives its answer away.
  const needsWords = hasWords && (active || clozeIndex >= 0);

  return (
    <div className={`ir-line${active ? " ir-line-active" : ""}`} ref={ref}>
      <p className="ir-line-text">
        {needsWords
          ? line.words.map((word, index) => (
            <Word
              cloze={index === clozeIndex}
              highlighted={active && positionMs >= word.startMs && positionMs < word.endMs}
              key={`${line.startMs}-${index}`}
              sung={active ? positionMs >= word.startMs : passed}
              word={word}
            />
          ))
          : line.text}
      </p>
      {showEnglish && <p className="ir-line-translation">{line.translation}</p>}

      <LineActions line={line} onAdd={onAdd} onPlayFrom={onPlayFrom} />
    </div>
  );
}));

Line.propTypes = {
  active: PropTypes.bool.isRequired,
  line: PropTypes.object.isRequired,
  mode: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  onPlayFrom: PropTypes.func.isRequired,
  passed: PropTypes.bool.isRequired,
  positionMs: PropTypes.number.isRequired,
  showEnglish: PropTypes.bool.isRequired,
};

// The whole song is in the DOM and the reel slides to centre whichever line is
// being sung. The earlier version re-sliced a window of lines each time the
// active line changed, which meant the active line stayed at roughly the same
// pixel while its neighbours were swapped underneath it - that's the jump.
// Sliding a static list is what makes the movement continuous.
//
// Rendering every line is affordable only because Line is memoised and just one
// of them is handed the clock.
export default function Lyrics({
  activeIndex, lines, mode, onAdd, onPlayFrom, positionMs, showEnglish, trackId,
}) {
  const scrollRef = useRef(null);
  const activeRef = useRef(null);
  // Skips the animation for the next centering. Set on mount and on every track
  // change: there is nothing worth watching in the trip from the end of one
  // song's lyrics to the start of the next, and animating it means the opening
  // line is already being sung while the reel is still travelling.
  const instantRef = useRef(true);
  // Set once the listener scrolls for themselves. While it's on, the song stops
  // dragging the view around under them.
  const [browsing, setBrowsing] = useState(false);

  const centerActive = useCallback(() => {
    const container = scrollRef.current;
    const el = activeRef.current;
    if (!container || !el) return;

    // Measured rather than computed: lines wrap, and the translation row appears
    // and disappears with the English toggle, so heights are never uniform.
    // An explicit behavior overrides the stylesheet's scroll-behavior, so the
    // reduced-motion preference has to be read here rather than left to CSS.
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const instant = instantRef.current || reduced;
    instantRef.current = false;

    container.scrollTo({
      behavior: instant ? "auto" : "smooth",
      top: el.offsetTop + el.offsetHeight / 2 - container.clientHeight / 2,
    });
  }, []);

  // Declared before the centering effect so it has already armed instantRef by
  // the time that one runs - layout effects fire in declaration order, and both
  // fire on the render where the track changed.
  useLayoutEffect(() => {
    instantRef.current = true;
    // A new song is a clean slate - nobody means to still be reading the last one.
    setBrowsing(false);
  }, [trackId]);

  useLayoutEffect(() => {
    if (!browsing) centerActive();
  }, [activeIndex, browsing, centerActive, mode, showEnglish, trackId]);

  // Before the first line is sung there is no active line, but the reel still
  // has to be somewhere - and the scroll position carries over from the previous
  // song, so without this a new track opens showing the middle of its lyrics.
  const focusIndex = Math.max(activeIndex, 0);

  if (mode === "off") return null;

  return (
    <div className="ir-lyrics-panel">
      {/* wheel and touchmove rather than scroll: scroll also fires for the
          auto-follow above, which would make the view stick the moment it moved
          on its own. These two only happen when a person does them. */}
      <div
        className="ir-lyrics"
        onTouchMove={() => setBrowsing(true)}
        onWheel={() => setBrowsing(true)}
        ref={scrollRef}
      >
        <div className="ir-lyrics-reel">
          {lines.map((line, index) => (
            <Line
              active={index === activeIndex}
              key={`${line.startMs}-${index}`}
              line={line}
              mode={mode}
              onAdd={onAdd}
              onPlayFrom={onPlayFrom}
              // Flips once, as the song crosses this line, so memo still skips
              // every line that isn't near the action.
              passed={index < activeIndex}
              // A stable value for every line but the active one, so memo can skip
              // them - they have no use for the clock.
              positionMs={index === activeIndex ? positionMs : 0}
              ref={index === focusIndex ? activeRef : null}
              showEnglish={showEnglish}
            />
          ))}
        </div>
      </div>

      {browsing && (
        <button className="ir-lyrics-back" onClick={() => setBrowsing(false)} type="button">
          Back to the current line
        </button>
      )}
    </div>
  );
}

Lyrics.propTypes = {
  activeIndex: PropTypes.number.isRequired,
  lines: PropTypes.arrayOf(PropTypes.object).isRequired,
  mode: PropTypes.oneOf(["read", "cloze", "off"]).isRequired,
  onAdd: PropTypes.func.isRequired,
  onPlayFrom: PropTypes.func.isRequired,
  positionMs: PropTypes.number.isRequired,
  showEnglish: PropTypes.bool.isRequired,
  trackId: PropTypes.string,
};

Lyrics.defaultProps = { trackId: null };
